/**
 * localStorage provider implementation.
 *
 * @module
 */
import { I18nError } from '@molecule/app-i18n';
import { getLogger } from '@molecule/app-logger';
/**
 * Creates a localStorage-based storage provider that implements the molecule StorageProvider interface.
 *
 * @example
 * ```ts
 * import { createLocalStorageProvider } from '@molecule/app-storage-localstorage'
 * import { setProvider } from '@molecule/app-storage'
 *
 * const storage = createLocalStorageProvider({
 *   prefix: 'myapp_',
 * })
 *
 * setProvider(storage)
 *
 * // Store data
 * await storage.set('user', { name: 'John', email: 'john@example.com' })
 *
 * // Retrieve data
 * const user = await storage.get<{ name: string; email: string }>('user')
 * ```
 * @param config - Optional configuration for key prefix, serialization, and storage backend.
 * @returns A `StorageProvider` backed by `localStorage` with an in-memory fallback for SSR.
 */
export function createLocalStorageProvider(config = {}) {
    const logger = getLogger('storage');
    const { prefix = '', serialize = JSON.stringify, deserialize = JSON.parse, codecs = [], storage = typeof window !== 'undefined' ? window.localStorage : undefined, } = config;
    // Helper to get prefixed key
    const getKey = (key) => `${prefix}${key}`;
    // The codec that owns this key, if one was configured. Keys are matched
    // UNPREFIXED — the same string the caller passes — so app config never has to
    // repeat the prefix.
    const codecFor = (key) => codecs.find((codec) => codec.matches(key));
    /** Encodes a value the way this key's codec (or the default pair) says. */
    const encode = (key, value) => {
        const codec = codecFor(key);
        return codec ? codec.encode(value) : serialize(value);
    };
    /** Decodes a stored string the way this key's codec (or the default pair) says. */
    const decode = (key, raw) => {
        const codec = codecFor(key);
        return codec ? codec.decode(raw) : deserialize(raw);
    };
    // Helper to strip prefix from key
    const stripPrefix = (key) => {
        if (prefix && key.startsWith(prefix)) {
            return key.slice(prefix.length);
        }
        return key;
    };
    // Check if storage is available
    const isAvailable = () => {
        if (!storage)
            return false;
        try {
            const testKey = '__storage_test__';
            storage.setItem(testKey, testKey);
            storage.removeItem(testKey);
            return true;
        }
        catch (error) {
            logger.warn('localStorage is not available', { error });
            return false;
        }
    };
    // In-memory fallback for SSR or unavailable localStorage
    const memoryStorage = new Map();
    const getStorage = () => {
        if (isAvailable() && storage) {
            return storage;
        }
        // Return in-memory fallback
        return {
            getItem: (key) => memoryStorage.get(key) ?? null,
            setItem: (key, value) => memoryStorage.set(key, value),
            removeItem: (key) => memoryStorage.delete(key),
            clear: () => memoryStorage.clear(),
            key: (index) => Array.from(memoryStorage.keys())[index] ?? null,
            get length() {
                return memoryStorage.size;
            },
        };
    };
    const provider = {
        async get(key) {
            try {
                const store = getStorage();
                const value = store.getItem(getKey(key));
                if (value === null) {
                    return null;
                }
                return decode(key, value);
            }
            catch (err) {
                logger.warn('Failed to deserialize stored value', key, err);
                return null;
            }
        },
        getSync(key) {
            try {
                const value = getStorage().getItem(getKey(key));
                if (value === null) {
                    return null;
                }
                return decode(key, value);
            }
            catch (err) {
                logger.warn('Failed to deserialize stored value', key, err);
                return null;
            }
        },
        async set(key, value) {
            try {
                const store = getStorage();
                const serialized = encode(key, value);
                store.setItem(getKey(key), serialized);
            }
            catch (error) {
                // Handle quota exceeded errors
                if (error instanceof Error && error.name === 'QuotaExceededError') {
                    logger.warn('Storage quota exceeded', key);
                    throw new I18nError('storage.error.quotaExceeded', { key }, `Storage quota exceeded when setting key "${key}"`, error);
                }
                throw error;
            }
        },
        async remove(key) {
            const store = getStorage();
            store.removeItem(getKey(key));
        },
        subscribe(key, listener) {
            // Only a real browser can report another tab's write, and only through
            // the `storage` event (it never fires in the tab that made the change).
            if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
                return () => { };
            }
            const prefixedKey = getKey(key);
            const onStorage = (event) => {
                // A different backend (sessionStorage) writing the same key is not our
                // change. `storageArea` is absent on a synthetic event — nothing to compare.
                if (event.storageArea && storage && event.storageArea !== storage)
                    return;
                // `key === null` is another tab's `clear()`, which removed every key.
                if (event.key !== null && event.key !== prefixedKey)
                    return;
                if (event.newValue === null) {
                    listener(null);
                    return;
                }
                try {
                    listener(decode(key, event.newValue));
                }
                catch (err) {
                    // Another tab wrote a value this provider cannot read: it is absent
                    // for us, the same contract as `get()`.
                    logger.warn('Failed to deserialize stored value', key, err);
                    listener(null);
                }
            };
            window.addEventListener('storage', onStorage);
            return () => window.removeEventListener('storage', onStorage);
        },
        async clear() {
            const store = getStorage();
            if (prefix) {
                // Only clear prefixed keys
                const keysToRemove = [];
                for (let i = 0; i < store.length; i++) {
                    const key = store.key(i);
                    if (key && key.startsWith(prefix)) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach((key) => store.removeItem(key));
            }
            else {
                store.clear();
            }
        },
        async keys() {
            const store = getStorage();
            const result = [];
            for (let i = 0; i < store.length; i++) {
                const key = store.key(i);
                if (key) {
                    if (prefix) {
                        if (key.startsWith(prefix)) {
                            result.push(stripPrefix(key));
                        }
                    }
                    else {
                        result.push(key);
                    }
                }
            }
            return result;
        },
        async getMany(keys) {
            const result = new Map();
            for (const key of keys) {
                const value = await provider.get(key);
                result.set(key, value);
            }
            return result;
        },
        async setMany(entries) {
            for (const [key, value] of entries) {
                await provider.set(key, value);
            }
        },
        async removeMany(keys) {
            for (const key of keys) {
                await provider.remove(key);
            }
        },
    };
    return provider;
}
/**
 * Creates a `sessionStorage`-based provider using the same implementation as `createLocalStorageProvider`
 * but backed by `window.sessionStorage` (data cleared when the browser tab closes).
 *
 * @example
 * ```ts
 * import { createSessionStorageProvider } from '@molecule/app-storage-localstorage'
 *
 * const storage = createSessionStorageProvider()
 * ```
 *
 * @param config - Optional configuration for key prefix and serialization.
 * @returns A `StorageProvider` backed by `sessionStorage`.
 */
export function createSessionStorageProvider(config = {}) {
    return createLocalStorageProvider({
        ...config,
        storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    });
}
/** Default `localStorage` provider created with no prefix and JSON serialization. */
export const provider = createLocalStorageProvider();
/**
 * Session storage provider.
 */
export const sessionProvider = createSessionStorageProvider();
//# sourceMappingURL=provider.js.map